import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  CheckCircle, XCircle, AlertTriangle, Clock, Award, 
  MessageSquare, Building2, User, ThumbsUp, ThumbsDown, Timer
} from 'lucide-react';
import { toast } from 'sonner';

const ValidationAutonomieStagiaire = ({ user }) => {
  const [autonomies, setAutonomies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAutonomie, setSelectedAutonomie] = useState(null);
  const [commentaire, setCommentaire] = useState('');
  const [processing, setProcessing] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);

  // Helper pour déterminer le type de proposition (compétence vs autonomie)
  const getTypeLabel = (sousCat) => {
    const sc = (sousCat || '').toLowerCase();
    if (sc.includes('autonomie')) return "l'autonomie";
    return 'la compétence';
  };

  useEffect(() => {
    loadAutonomies();
  }, []);

  const loadAutonomies = async () => {
    try {
      const response = await axios.get(`/autonomies-en-attente/stagiaire/${user.user_id}`);
      setAutonomies(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = async (autonomie, accepte) => {
    setProcessing(true);
    try {
      await axios.post('/validations-stagiaire', {
        categorie: autonomie.categorie,
        sous_categorie: autonomie.sous_categorie,
        accepte,
        commentaire
      });
      
      toast.success(accepte ? 'Autonomie acceptée !' : 'Autonomie refusée');
      setSelectedAutonomie(null);
      setCommentaire('');
      loadAutonomies();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    } finally {
      setProcessing(false);
    }
  };

  // Séparer les autonomies en attente et celles déjà répondues
  const enAttente = autonomies.filter(a => a.en_attente);
  const repondues = autonomies.filter(a => !a.en_attente);

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="validation-autonomie-stagiaire">
      <div>
        <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Validation d'Autonomie</h1>
        <p className="text-slate-600">Acceptez ou refusez les compétences validées par votre tuteur et votre entreprise</p>
      </div>

      {/* Compteurs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center gap-4">
          <Clock className="h-8 w-8 text-orange-500" />
          <div>
            <p className="text-2xl font-bold text-orange-700">{enAttente.length}</p>
            <p className="text-sm text-orange-600">En attente de votre réponse</p>
          </div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-4">
          <CheckCircle className="h-8 w-8 text-green-500" />
          <div>
            <p className="text-2xl font-bold text-green-700">{repondues.filter(a => a.stagiaire_reponse === true).length}</p>
            <p className="text-sm text-green-600">Acceptées</p>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-4">
          <XCircle className="h-8 w-8 text-red-500" />
          <div>
            <p className="text-2xl font-bold text-red-700">{repondues.filter(a => a.stagiaire_reponse === false).length}</p>
            <p className="text-sm text-red-600">Refusées</p>
          </div>
        </div>
      </div>

      {/* Propositions en attente */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-orange-500" />
          En attente de votre réponse
        </h2>

        {enAttente.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
            <p className="text-slate-500">Aucune proposition en attente</p>
            <p className="text-sm text-slate-400">Toutes les validations ont été traitées</p>
          </div>
        ) : (
          <div className="space-y-4">
            {enAttente.map((autonomie, idx) => (
              <div 
                key={idx} 
                className="border-2 border-orange-200 bg-orange-50/30 rounded-lg p-4"
                data-testid={`autonomie-${autonomie.categorie}-${autonomie.sous_categorie}`}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="h-5 w-5 text-[#8DC63F]" />
                      <h3 className="font-semibold text-[#0F172A]">{autonomie.categorie}</h3>
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-[#8DC63F]/20 text-[#5a8f1f]">
                        Proposition de valider {getTypeLabel(autonomie.sous_categorie)}
                      </span>
                    </div>
                    <p className="text-slate-700 mb-3">{autonomie.sous_categorie}</p>
                    
                    {/* Statuts tuteur et entreprise */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                        <User className="h-3 w-3" />
                        <span>Tuteur: Validé</span>
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                        <Building2 className="h-3 w-3" />
                        <span>Entreprise: Validé</span>
                      </div>
                      {autonomie.heures_encadrement > 0 && (
                        <div
                          className="flex items-center gap-1 px-2 py-1 bg-[#8DC63F]/15 text-[#5a8f1f] text-xs rounded-full font-semibold"
                          title="Cumul des heures encadrées saisies par votre tuteur dans le carnet"
                          data-testid={`heures-encadrees-${autonomie.sous_categorie}`}
                        >
                          <Timer className="h-3 w-3" />
                          <span>{autonomie.heures_encadrement} h encadrées</span>
                        </div>
                      )}
                    </div>

                    {/* Appréciations */}
                    {(autonomie.appreciation_tuteur || autonomie.appreciation_entreprise) && (
                      <div className="space-y-2 mt-3">
                        {autonomie.appreciation_tuteur && (
                          <div className="text-sm bg-white p-2 rounded border-l-2 border-green-500">
                            <span className="font-medium text-slate-700">Tuteur:</span>
                            <span className="text-slate-600 italic ml-1">"{autonomie.appreciation_tuteur}"</span>
                          </div>
                        )}
                        {autonomie.appreciation_entreprise && (
                          <div className="text-sm bg-white p-2 rounded border-l-2 border-blue-500">
                            <span className="font-medium text-slate-700">Entreprise:</span>
                            <span className="text-slate-600 italic ml-1">"{autonomie.appreciation_entreprise}"</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Boutons d'action */}
                  <div className="flex flex-col gap-2 min-w-[150px]">
                    <Button 
                      onClick={() => setSelectedAutonomie(autonomie)}
                      className="bg-[#8DC63F] hover:bg-[#7db336] text-white"
                      data-testid={`btn-repondre-${autonomie.sous_categorie}`}
                    >
                      Répondre
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de réponse */}
      {selectedAutonomie && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full shadow-xl">
            <h3 className="text-xl font-semibold text-[#0F172A] mb-2">
              Confirmer votre réponse
            </h3>
            <p className="text-slate-600 mb-1">
              {selectedAutonomie.categorie} - {selectedAutonomie.sous_categorie}
            </p>
            <p className="text-sm font-medium text-[#8DC63F] mb-4">
              Proposition de valider {getTypeLabel(selectedAutonomie.sous_categorie)}
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Commentaire {selectedAction === 'refuser' ? <span className="text-red-500">* (obligatoire pour un refus)</span> : '(optionnel)'}
              </label>
              <textarea
                value={commentaire}
                onChange={(e) => setCommentaire(e.target.value)}
                rows={3}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F] ${
                  selectedAction === 'refuser' && !commentaire.trim() ? 'border-red-300 bg-red-50/50' : 'border-slate-200'
                }`}
                placeholder={selectedAction === 'refuser' ? "Indiquez le motif de votre refus (obligatoire)..." : "Ajoutez un commentaire si vous le souhaitez..."}
                data-testid="textarea-commentaire-stagiaire"
              />
              {selectedAction === 'refuser' && !commentaire.trim() && (
                <p className="text-xs text-red-500 mt-1">Vous devez expliquer pourquoi vous refusez cette autonomie</p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => { setSelectedAction('accepter'); handleResponse(selectedAutonomie, true); }}
                disabled={processing}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm"
                data-testid="btn-accepter"
              >
                <ThumbsUp className="h-4 w-4 mr-1" />
                Accepter
              </Button>
              <Button
                onClick={() => {
                  if (!commentaire.trim()) {
                    setSelectedAction('refuser');
                    toast.error('Veuillez indiquer un motif de refus');
                    return;
                  }
                  setSelectedAction('refuser');
                  handleResponse(selectedAutonomie, false);
                }}
                disabled={processing}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm"
                data-testid="btn-refuser"
              >
                <ThumbsDown className="h-4 w-4 mr-1" />
                Refuser
              </Button>
            </div>
            <Button
              onClick={() => {
                setSelectedAutonomie(null);
                setCommentaire('');
                setSelectedAction(null);
              }}
              variant="outline"
              className="w-full mt-2"
            >
              Annuler
            </Button>
          </div>
        </div>
      )}

      {/* Historique des réponses */}
      {repondues.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-slate-500" />
            Historique de vos réponses
          </h2>
          <div className="space-y-3">
            {repondues.map((autonomie, idx) => (
              <div 
                key={idx} 
                className={`p-4 rounded-lg border ${
                  autonomie.stagiaire_reponse 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[#0F172A]">{autonomie.categorie} - {autonomie.sous_categorie}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <p className="text-xs text-slate-500">
                        {autonomie.stagiaire_date && new Date(autonomie.stagiaire_date).toLocaleDateString('fr-FR')}
                      </p>
                      {autonomie.heures_encadrement > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#5a8f1f]">
                          <Timer className="h-3 w-3" />
                          {autonomie.heures_encadrement} h encadrées
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                    autonomie.stagiaire_reponse 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {autonomie.stagiaire_reponse ? (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        Accepté
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4" />
                        Refusé
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidationAutonomieStagiaire;
